import { Outlet, useNavigate, useOutletContext } from "react-router";
import { useEffect } from "react";
import type { SystemCapability } from "@shared-domain/unizo/permissions";
import type { UnizoFeatureFlagKey } from "@shared-domain/unizo/featureFlags";
import { useRootLoader } from "@/hooks/useRootLoader";
import { checkCan } from "@/utils/role";
import { resolveTenantFeatureFlag } from "@/utils/tenantFeatureFlag";
import { ROUTE_403 } from "@/utils/const";

/**
 * PRODUCT_DEV-1236: できること（役割とできることの表）と、テナントの機能フラグの両方で絞るルートのガード。
 * どちらかが無ければ 403 へ送る（メニューも同じ2つで出し分けている・`resolveAdminMenuItems`）。
 * 段で絞るルートは `SystemRoleGuard`（SysadminGuard.tsx）。
 *
 * ⚠ ここは表示の出し分けだけ。認可の正はサーバ（同じ表とフラグで判定する）。
 */
export function CapabilityGuard({
  capability,
  featureFlag,
}: {
  capability: SystemCapability;
  featureFlag?: UnizoFeatureFlagKey;
}) {
  const { auth } = useRootLoader();
  const navigate = useNavigate();
  const context = useOutletContext();
  const allowed =
    checkCan(auth, capability) && (!featureFlag || resolveTenantFeatureFlag(auth, featureFlag));

  useEffect(() => {
    if (!allowed) {
      navigate(ROUTE_403, { replace: true });
    }
  }, [allowed, navigate]);

  if (!allowed) {
    return null;
  }

  return <Outlet context={context} />;
}

export default CapabilityGuard;
